import { useEffect, useRef, useState } from "react";
import { AlertCircle, LoaderCircle, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { PORTAL_ELEMENTS_APPEARANCE } from "./appearance";

export type AdsSurface = "campaigns" | "reporting" | "creator" | "billing" | "tracking";

type ElementsToken = { accountId: string; accessToken: string; expiresAt: string };
type WebsiteVerifyEvent = { target?: string; url?: string };
type PixelScanEvent = { url?: string };
type WebsitesLoadedEvent = { domains?: string[] };
type ElementError = { message?: string; code?: string; sourceKey?: string };
export type PendingWebsite = { domain: string; kind: "events" };
type PixelScanResponse = { installed?: boolean; pixel_present?: boolean; scoped?: boolean };
type ElementHandle = { mount(target: HTMLElement | string): void; destroy(): void };
type OverlayHandle = { open(): void; close?(): void; destroy?(): void };
type AdsHandle = {
  create(name: string, options?: Record<string, unknown>): ElementHandle & { create(name: string, options?: Record<string, unknown>): ElementHandle };
  createOverlay(name: string, options?: Record<string, unknown>): OverlayHandle;
  update(options: Record<string, unknown>): void;
};
type WebsitesHandle = {
  create(name: string, options?: Record<string, unknown>): ElementHandle;
  update(options: Record<string, unknown>): void;
};
type ElementsFactory = (options?: Record<string, unknown>) => {
  ads: { create(options: Record<string, unknown>): AdsHandle };
  websites: { create(options: Record<string, unknown>): WebsitesHandle };
  // Pre-release tracking element family (people + events); absent in older bundles.
  tracking?: { create(options: Record<string, unknown>): WebsitesHandle };
};

type InsightState = "loading" | "ready" | "failed";

declare global {
  interface Window { WhopElements?: ElementsFactory }
}

// cdn.whop.com replaced js.whop.cloud, which some ISPs block outright.
const scriptUrl = "https://cdn.whop.com/elements/amber/elements.js";
let loader: Promise<ElementsFactory> | null = null;
let sharedToken: ElementsToken | null = null;
let sharedTokenRequest: Promise<ElementsToken> | null = null;
let sharedTokenGeneration = 0;

export function clearWhopAccessToken() {
  sharedTokenGeneration += 1;
  sharedToken = null;
  sharedTokenRequest = null;
}

function reusableToken(token: ElementsToken | null) {
  return Boolean(token && Date.parse(token.expiresAt) > Date.now() + 5 * 60_000);
}

async function getWhopAccessToken(surface: AdsSurface, forceRefresh = false): Promise<ElementsToken> {
  if (!forceRefresh && reusableToken(sharedToken)) return sharedToken as ElementsToken;
  if (sharedTokenRequest) return sharedTokenRequest;
  if (forceRefresh) sharedToken = null;
  const generation = sharedTokenGeneration;
  const request = api<ElementsToken>("/api/ads/elements-token", {
    method: "POST",
    body: JSON.stringify({ surface: tokenSurface(surface) }),
  }).then((token) => {
    if (generation === sharedTokenGeneration) sharedToken = token;
    return token;
  });
  sharedTokenRequest = request;
  try {
    return await request;
  } finally {
    if (sharedTokenRequest === request) sharedTokenRequest = null;
  }
}

export function normalizeWebsiteDomain(value: string): string {
  const input = value.trim();
  if (!input) return "";
  try {
    return new URL(input.includes("://") ? input : `https://${input}`).hostname.toLowerCase().replace(/\.$/, "");
  } catch {
    return "";
  }
}

export function pendingWebsitesAfterScan(pending: PendingWebsite[], existingDomains: string[], scannedUrl: string): PendingWebsite[] {
  const domain = normalizeWebsiteDomain(scannedUrl);
  if (!domain) return pending;
  const existing = new Set(existingDomains.map(normalizeWebsiteDomain));
  if (existing.has(domain) || pending.some((website) => normalizeWebsiteDomain(website.domain) === domain)) return pending;
  return [...pending, { domain, kind: "events" }];
}

export function reconcilePendingWebsites(pending: PendingWebsite[], existingDomains: string[]): PendingWebsite[] {
  const existing = new Set(existingDomains.map(normalizeWebsiteDomain));
  return pending.filter((website) => !existing.has(normalizeWebsiteDomain(website.domain)));
}

function loadElements(): Promise<ElementsFactory> {
  if (window.WhopElements) return Promise.resolve(window.WhopElements);
  if (loader) return loader;
  loader = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>("script[data-ads-portal-elements]");
    const script = existing || document.createElement("script");
    const timeout = window.setTimeout(() => {
      loader = null;
      reject(new Error("The ads workspace took too long to load."));
    }, 20_000);
    const finish = () => {
      window.clearTimeout(timeout);
      if (window.WhopElements) resolve(window.WhopElements);
      else reject(new Error("The ads workspace is unavailable."));
    };
    const fail = () => {
      window.clearTimeout(timeout);
      loader = null;
      reject(new Error("The ads workspace could not load."));
    };
    script.addEventListener("load", finish, { once: true });
    script.addEventListener("error", fail, { once: true });
    if (!existing) {
      script.src = scriptUrl;
      script.async = true;
      script.dataset.adsPortalElements = "true";
      document.head.appendChild(script);
    }
  });
  return loader;
}

function tokenSurface(surface: AdsSurface) {
  return surface === "creator" ? "campaigns" : surface;
}

type Props = {
  surface: AdsSurface;
  onReady?: () => void;
  onClose?: () => void;
  onLaunched?: () => void;
  onError?: (message: string) => void;
  onBillingChanged?: () => void;
};

const GENERIC_ELEMENT_ERROR = "This view could not load. Try again.";

// The embedded elements render their own error fallback and, on some framework
// refusals, try to raise a toast. There is no toast provider inside the Whop
// iframe, so those toasts go into the void. We therefore switch on the refusal
// `code`/`sourceKey` (never the message text, which may be absent) so every
// failure surfaces a clear, branded message in the portal's own error panel.
export function describeElementError(error?: ElementError) {
  const code = error?.code?.trim();
  if (code === "HOST_SOURCE_FAILED") {
    return error?.sourceKey?.trim() === "accessToken"
      ? "This view's secure session expired. Refresh and try again."
      : "The portal couldn't supply this view with the data it needs. Refresh and try again.";
  }
  const message = error?.message?.trim();
  if (message && message.length <= 300) return message;
  return GENERIC_ELEMENT_ERROR;
}

export function WhopAdsSurface({ surface, onReady, onClose, onLaunched, onError, onBillingChanged }: Props) {
  const firstTarget = useRef<HTMLDivElement>(null);
  const secondTarget = useRef<HTMLDivElement>(null);
  const peopleTarget = useRef<HTMLDivElement>(null);
  const eventsTarget = useRef<HTMLDivElement>(null);
  const [attempt, setAttempt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [readyCount, setReadyCount] = useState(0);
  const [insights, setInsights] = useState<{ people: InsightState; events: InsightState }>({ people: "loading", events: "loading" });
  const [trackingTab, setTrackingTab] = useState<"setup" | "people" | "events">("setup");
  const callbacks = useRef({ onReady, onClose, onLaunched, onError, onBillingChanged });

  useEffect(() => {
    callbacks.current = { onReady, onClose, onLaunched, onError, onBillingChanged };
  }, [onReady, onClose, onLaunched, onError, onBillingChanged]);

  useEffect(() => {
    let cancelled = false;
    let refreshTimer = 0;
    const handles: ElementHandle[] = [];
    let overlay: OverlayHandle | null = null;
    let updateAccess: ((token: string) => void) | null = null;
    const expected = surface === "campaigns" || surface === "reporting" || surface === "tracking" ? 2 : 1;
    const ready = () => {
      if (cancelled) return;
      setReadyCount((value) => {
        const next = Math.min(expected, value + 1);
        if (next >= expected) {
          setLoading(false);
          callbacks.current.onReady?.();
        }
        return next;
      });
    };
    let creatorReady = false;
    const failed = (elementError?: ElementError) => {
      if (cancelled) return;
      // Once the creator is open, the hosted flow renders launch rejections
      // inline itself; closing it from under the client would hide that error.
      if (surface === "creator" && creatorReady) return;
      const message = describeElementError(elementError);
      setError(message);
      setLoading(false);
      callbacks.current.onError?.(message);
      if (surface === "creator") {
        overlay?.close?.();
        callbacks.current.onClose?.();
      }
    };
    const boot = async () => {
      setLoading(true);
      setError("");
      setReadyCount(0);
      setInsights({ people: "loading", events: "loading" });
      try {
        const [token, factory] = await Promise.all([
          getWhopAccessToken(surface),
          loadElements(),
        ]);
        if (cancelled) return;
        const elements = factory({ locale: "en", appearance: PORTAL_ELEMENTS_APPEARANCE });

        if (surface === "tracking") {
          let existingDomains: string[] = [];
          let pendingWebsites: PendingWebsite[] = [];
          const websites = elements.websites.create({
            accountId: token.accountId,
            accessToken: token.accessToken,
            locale: "en",
            appearance: PORTAL_ELEMENTS_APPEARANCE,
          });
          updateAccess = (accessToken) => websites.update({ accessToken });
          if (firstTarget.current) {
            const pixel = websites.create("pixel-setup", {
              accountId: token.accountId,
              accessToken: token.accessToken,
              initialStep: "install",
              showIntro: true,
              showInviteDeveloper: false,
              onScanned: ({ url = "" }: PixelScanEvent) => {
                const next = pendingWebsitesAfterScan(pendingWebsites, existingDomains, url);
                if (next === pendingWebsites) return;
                pendingWebsites = next;
                websites.update({ pendingWebsites });
              },
              onFinished: () => undefined,
              onEventVerified: () => undefined,
              onReady: ready,
              onError: failed,
            });
            pixel.mount(firstTarget.current);
            handles.push(pixel);
          }
          if (secondTarget.current) {
            websites.update({ includeApps: false, pendingWebsites, showVerify: true, verifyTarget: "", verifyStatus: "idle" });
            const sites = websites.create("websites", {
              onReady: ready,
              onError: failed,
              onWebsitesLoaded: ({ domains = [] }: WebsitesLoadedEvent) => {
                existingDomains = domains;
                const next = reconcilePendingWebsites(pendingWebsites, existingDomains);
                if (next.length === pendingWebsites.length) return;
                pendingWebsites = next;
                websites.update({ pendingWebsites });
              },
              onVerifyRequested: async ({ target = "", url = "" }: WebsiteVerifyEvent) => {
                if (!target || !url) return;
                const popup = window.open(url, "_blank");
                if (popup) popup.opener = null;
                websites.update({ verifyTarget: target, verifyStatus: popup ? "waiting" : "blocked" });
                if (!popup) return;
                try {
                  const scan = await api<PixelScanResponse>("/api/ads/pixel-scan", {
                    method: "POST",
                    body: JSON.stringify({ url }),
                  });
                  const installed = scan.installed ?? scan.pixel_present ?? false;
                  websites.update({ verifyTarget: target, verifyStatus: installed && scan.scoped !== false ? "connected" : "not-detected" });
                } catch {
                  websites.update({ verifyTarget: target, verifyStatus: "not-detected" });
                }
              },
            });
            sites.mount(secondTarget.current);
            handles.push(sites);
          }
          if (elements.tracking) {
            const trackingRoot = elements.tracking.create({
              accountId: token.accountId,
              accessToken: token.accessToken,
              locale: "en",
              appearance: PORTAL_ELEMENTS_APPEARANCE,
            });
            const refreshWebsites = updateAccess;
            updateAccess = (accessToken) => {
              refreshWebsites?.(accessToken);
              trackingRoot.update({ accessToken });
            };
            // People and events are pre-release views: a failure hides that
            // card only, never the pixel and websites setup beside it.
            const mountInsight = (name: "people" | "events", target: HTMLDivElement | null) => {
              if (!target) return;
              try {
                const insight = trackingRoot.create(name, {
                  onReady: () => { if (!cancelled) setInsights((current) => ({ ...current, [name]: "ready" })); },
                  onError: () => { if (!cancelled) setInsights((current) => ({ ...current, [name]: "failed" })); },
                });
                insight.mount(target);
                handles.push(insight);
              } catch {
                if (!cancelled) setInsights((current) => ({ ...current, [name]: "failed" }));
              }
            };
            mountInsight("people", peopleTarget.current);
            mountInsight("events", eventsTarget.current);
          } else {
            setInsights({ people: "failed", events: "failed" });
          }
        } else {
          const ads = elements.ads.create({
            accountId: token.accountId,
            accessToken: token.accessToken,
            locale: "en",
            appearance: PORTAL_ELEMENTS_APPEARANCE,
            onBillingChanged: () => callbacks.current.onBillingChanged?.(),
          });
          updateAccess = (accessToken) => ads.update({ accessToken });
          if (surface === "campaigns" && firstTarget.current && secondTarget.current) {
            const reporting = ads.create("reporting", { period: "last_30_days" });
            const chart = reporting.create("chart", { metric: "spend", onReady: ready, onError: failed });
            const table = reporting.create("table", { useCampaignCreator: true, onReady: ready, onError: failed });
            chart.mount(firstTarget.current);
            table.mount(secondTarget.current);
            handles.push(chart, table, reporting);
          } else if (surface === "reporting" && firstTarget.current && secondTarget.current) {
            const reporting = ads.create("reporting", { period: "last_30_days" });
            const chart = reporting.create("chart", { metric: "spend", onReady: ready, onError: failed });
            const table = reporting.create("table", { useCampaignCreator: true, onReady: ready, onError: failed });
            chart.mount(firstTarget.current);
            table.mount(secondTarget.current);
            handles.push(chart, table, reporting);
          } else if (surface === "billing" && firstTarget.current) {
            const billing = ads.create("billing-setup", { onReady: ready, onError: failed });
            billing.mount(firstTarget.current);
            handles.push(billing);
          } else if (surface === "creator") {
            overlay = ads.createOverlay("campaign-creator", {
              initialStep: "campaign-settings",
              onReady: () => { creatorReady = true; ready(); },
              onExited: () => callbacks.current.onClose?.(),
              onLaunched: () => {
                callbacks.current.onLaunched?.();
                callbacks.current.onClose?.();
              },
              onError: failed,
            });
            document.documentElement.classList.add("elements-creator-open");
            overlay.open();
          }
        }

        const refresh = (expiresAt: string) => {
          const delay = Math.max(30_000, Date.parse(expiresAt) - Date.now() - 5 * 60_000);
          refreshTimer = window.setTimeout(async () => {
            try {
              const fresh = await getWhopAccessToken(surface, true);
              if (!cancelled) {
                updateAccess?.(fresh.accessToken);
                refresh(fresh.expiresAt);
              }
            } catch {
              if (!cancelled) setError("This view needs to be reopened. Refresh and try again.");
            }
          }, delay);
        };
        refresh(token.expiresAt);
      } catch (caught) {
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : "The ads workspace could not load.");
          setLoading(false);
        }
      }
    };
    void boot();
    return () => {
      cancelled = true;
      window.clearTimeout(refreshTimer);
      overlay?.close?.();
      overlay?.destroy?.();
      document.documentElement.classList.remove("elements-creator-open");
      for (const handle of handles) handle.destroy();
    };
  }, [surface, attempt]);

  const ready = readyCount >= (surface === "campaigns" || surface === "reporting" || surface === "tracking" ? 2 : 1);
  const frameState = ready ? "is-ready" : "is-loading";
  // A failed pre-release view falls back to the setup tab and loses its tab.
  const activeTrackingTab = trackingTab !== "setup" && insights[trackingTab] === "failed" ? "setup" : trackingTab;
  const trackingHidden = (tab: "setup" | "people" | "events") => (activeTrackingTab === tab ? "" : " is-hidden");
  return (
    <div className={`whop-surface whop-surface--${surface} ${ready ? "is-ready" : ""}`}>
      {loading && surface !== "creator" && <div className="surface-state"><LoaderCircle className="spin" /><strong>Opening this view</strong><span>This usually takes a few seconds.</span></div>}
      {error && <div className="surface-state surface-state--error"><AlertCircle /><strong>Couldn’t open this view</strong><span>{error}</span><button className="button button--secondary button--small" type="button" onClick={() => setAttempt((value) => value + 1)}><RefreshCw /> Try again</button></div>}
      {surface === "campaigns" && <><section className={`elements-card elements-card--chart ${frameState}`} aria-busy={!ready}><div className="elements-target elements-target--chart" ref={firstTarget} /></section><section className={`elements-card elements-card--table ${frameState}`} aria-busy={!ready}><div className="elements-target elements-target--table" ref={secondTarget} /></section></>}
      {surface === "reporting" && <><section className={`elements-card elements-card--chart ${frameState}`} aria-busy={!ready}><div className="elements-target elements-target--chart" ref={firstTarget} /></section><section className={`elements-card elements-card--table ${frameState}`} aria-busy={!ready}><div className="elements-target elements-target--table" ref={secondTarget} /></section></>}
      {surface === "billing" && <section className={`elements-card elements-card--billing ${frameState}`} aria-busy={!ready}><div className="elements-target elements-target--billing" ref={firstTarget} /></section>}
      {surface === "tracking" && <>
        {!error && <nav className="tracking-tabs" role="tablist" aria-label="Tracking views">
          <button role="tab" type="button" aria-selected={activeTrackingTab === "setup"} className={activeTrackingTab === "setup" ? "is-active" : ""} onClick={() => setTrackingTab("setup")}>Pixel &amp; websites</button>
          {insights.people !== "failed" && <button role="tab" type="button" aria-selected={activeTrackingTab === "people"} className={activeTrackingTab === "people" ? "is-active" : ""} onClick={() => setTrackingTab("people")}>People</button>}
          {insights.events !== "failed" && <button role="tab" type="button" aria-selected={activeTrackingTab === "events"} className={activeTrackingTab === "events" ? "is-active" : ""} onClick={() => setTrackingTab("events")}>Events</button>}
        </nav>}
        <section className={`elements-card elements-card--pixel ${frameState}${trackingHidden("setup")}`} aria-busy={!ready}><div className="elements-target elements-target--pixel" ref={firstTarget} /></section>
        <section className={`elements-card elements-card--websites ${frameState}${trackingHidden("setup")}`} aria-busy={!ready}><div className="elements-target elements-target--websites" ref={secondTarget} /></section>
        {insights.people !== "failed" && <section className={`elements-card elements-card--people ${insights.people === "ready" ? "is-ready" : "is-loading"}${trackingHidden("people")}`} aria-busy={insights.people !== "ready"}><div className="elements-target elements-target--people" ref={peopleTarget} /></section>}
        {insights.events !== "failed" && <section className={`elements-card elements-card--events ${insights.events === "ready" ? "is-ready" : "is-loading"}${trackingHidden("events")}`} aria-busy={insights.events !== "ready"}><div className="elements-target elements-target--events" ref={eventsTarget} /></section>}
      </>}
    </div>
  );
}
